import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import translations from "../i18n/translations";

const LanguageContext = createContext();

const STORAGE_KEY = "songcraft_language";
const DEFAULT_LANG = "sl";
const SUPPORTED_LANGS = Object.keys(translations);

/**
 * Resolve a dot-notation key against a nested object.
 * e.g. resolve("landing.hero.title", translations.en)  =>  "Create Your Own Song in Minutes"
 */
function resolve(path, obj) {
  return path.split(".").reduce((acc, part) => {
    if (acc && typeof acc === "object" && part in acc) {
      return acc[part];
    }
    return undefined;
  }, obj);
}

function getInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch {
    // localStorage may be blocked
  }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  const setLang = useCallback((newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) {
      console.warn(`[LanguageContext] Unsupported language: "${newLang}". Supported: ${SUPPORTED_LANGS.join(", ")}`);
      return;
    }
    setLangState(newLang);
    try {
      localStorage.setItem(STORAGE_KEY, newLang);
    } catch {
      // localStorage may be blocked
    }
  }, []);

  /**
   * Translate a key using dot notation.
   *
   * Usage:
   *   t("landing.hero.title")                          => translated string
   *   t("songCreator.lyrics.write.charCount", { count: 142 })  => "142 characters"
   *
   * Interpolation: any {{placeholder}} in the translated string is replaced
   * with the matching key from the `params` object.
   *
   * Falls back to the English string if the key is missing in the current
   * language. Returns the raw key if it cannot be resolved at all.
   */
  const t = useCallback(
    (key, params) => {
      let value = resolve(key, translations[lang]);

      // Fallback to English
      if (value === undefined && lang !== DEFAULT_LANG) {
        value = resolve(key, translations[DEFAULT_LANG]);
      }

      // If still nothing, return the key itself so it's visible during dev
      if (value === undefined) {
        console.warn(`[LanguageContext] Missing translation key: "${key}"`);
        return key;
      }

      // Interpolate {{placeholders}}
      if (typeof value === "string" && params) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, placeholder) =>
          params[placeholder] !== undefined ? String(params[placeholder]) : `{{${placeholder}}}`
        );
      }

      return value;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access the language context.
 *
 * Returns: { lang, setLang, t }
 *   - lang    : current language code ("en" | "sl")
 *   - setLang : function to switch language
 *   - t       : translation function  t(key, params?)
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a <LanguageProvider>");
  }
  return context;
}

export default LanguageContext;
